import { styled } from "../../styles";

/* ---------------- Inline trigger (mirrors the form's other selects) --------------- */

export const TriggerBox = styled("button", {
  all: "unset",
  boxSizing: "border-box",
  cursor: "pointer",

  display: "flex",
  alignItems: "center",
  gap: "8px",

  height: "42px",
  padding: "0 11px",

  backgroundColor: "$white",
  border: "2px solid $gray-50",
  borderRadius: "6px",

  fontFamily: "$openSans",
  fontSize: "14px",
  color: "$black",

  "&:hover": {
    borderColor: "var(--colors-portal-dropdown-selected-border)",
  },

  "&:focus-visible": {
    borderColor: "var(--colors-portal-dropdown-selected-border)",
  },

  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.6,
  },

  svg: {
    flexShrink: 0,
    marginLeft: "auto",
  },

  variants: {
    hasError: {
      true: {
        borderColor: "$error-light",
      },
    },

    // Card look: plain text (one item per line), no select box — matches the
    // patient-details cards' value display. Still clickable to open the modal.
    compact: {
      true: {
        height: "auto",
        marginTop: "4px",
        padding: "4px 8px",
        alignItems: "flex-start",

        fontSize: "12px",
        fontWeight: 400,
        color: "$gray-90",

        backgroundColor: "transparent",
        border: "none",
        borderRadius: "6px",

        "&:hover": {
          backgroundColor: "$gray-30",
        },
      },
    },
  },
});

// One-selected-item-per-line list for the card trigger.
export const CardSummary = styled("span", {
  flex: 1,
  minWidth: 0,

  textAlign: "left",
  whiteSpace: "pre-line",
  wordBreak: "break-word",
});

export const Placeholder = styled("span", {
  flex: 1,
  minWidth: 0,

  color: "$gray-70",

  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const SelectedSummary = styled("span", {
  flex: 1,
  minWidth: 0,

  color: "$black",

  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

/* ----------------------------------- Modal ----------------------------------- */

export const ModalContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
  fontFamily: "$openSans",
});

export const ModalTitle = styled("p", {
  margin: 0,
  color: "$black",
  fontFamily: "$openSans",
  fontSize: "16px",
  lineHeight: "22px",
  fontWeight: "600",
});

export const SelectedChipsContainer = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "6px",
  maxHeight: "120px",
  overflowY: "auto",

  // The chips use the shared Tag (themed via $primary-* tokens). Re-skin them
  // here with the dropdown "selected" tokens so the selected region matches the
  // dropdown's selected rows. Scoped to this container — the global Tag stays
  // unchanged. Target the inner StyledTag div + its label span.
  "& > div > div": {
    backgroundColor: "var(--colors-portal-dropdown-popup-selected-background)",
    borderColor: "var(--colors-portal-dropdown-popup-selected-background)",
    color: "var(--colors-portal-dropdown-popup-selected-icon)",
  },
});

export const SearchWrapper = styled("div", {
  width: "100%",
});

export const ResultsList = styled("div", {
  display: "flex",
  flexDirection: "column",

  maxHeight: "300px",
  overflowY: "auto",

  border: "1px solid $gray-50",
  borderRadius: "8px",
});

export const ResultRow = styled("button", {
  all: "unset",
  boxSizing: "border-box",
  cursor: "pointer",

  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",

  padding: "10px 14px",

  fontFamily: "$openSans",
  fontSize: "13px",
  color: "$primary-100",

  borderBottom: "1px solid $gray-40",

  "&:last-child": {
    borderBottom: "none",
  },

  "&:hover": {
    backgroundColor: "$gray-30",
  },

  svg: {
    flexShrink: 0,
  },

  variants: {
    selected: {
      true: {
        backgroundColor:
          "var(--colors-portal-dropdown-popup-selected-background)",

        "&:hover": {
          backgroundColor:
            "var(--colors-portal-dropdown-popup-selected-background)",
        },
      },
    },
  },
});

export const ResultLabel = styled("span", {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const HintText = styled("p", {
  margin: 0,
  padding: "12px",
  textAlign: "center",

  fontFamily: "$openSans",
  fontSize: "12px",
  color: "$gray-90",
});

export const CapNote = styled("p", {
  margin: "-8px 0 0",
  fontFamily: "$openSans",
  fontSize: "11px",
  color: "$gray-90",
});

export const Footer = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",

  // Force the "Done" button label white regardless of the tenant's button-text
  // token. The label lives in a <p> inside the button, so target it directly.
  "button, button p": {
    color: "$white !important",
  },
});
